import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  Badge,
  Popover,
  Empty,
  Skeleton,
  Tooltip,
  Button,
  Typography,
} from "antd";
import {
  BellOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  LoadingOutlined,
  FileExcelOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import {useDispatch, useSelector} from "react-redux";
import moment from "moment";
import {
  AppDispatch,
  getExportRequestList,
  getExportRequestListRequest,
} from "@src/redux";
import * as TYPES from "@src/redux/types";

const POLL_MS = 30_000;
const POPOVER_WIDTH = 380;
const POPOVER_MAX_HEIGHT = 460;

const BRAND = "#319795";
const BRAND_DARK = "#22656a";

type StatusName = "Pending" | "Processing" | "Completed" | "Failed";

const STATUS_META: Record<
  StatusName,
  {
    accent: string;
    bg: string;
    border: string;
    icon: React.ReactNode;
    label: string;
    dot: string;
  }
> = {
  Pending: {
    accent: "#1d4ed8",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: <ClockCircleOutlined />,
    label: "Pending",
    dot: "#3b82f6",
  },
  Processing: {
    accent: "#7e3af2",
    bg: "#f4ebff",
    border: "#d6bbfb",
    icon: <LoadingOutlined spin />,
    label: "Processing",
    dot: "#a855f7",
  },
  Completed: {
    accent: "#0a7c4d",
    bg: "#ecfdf3",
    border: "#a6e9c5",
    icon: <CheckCircleFilled />,
    label: "Ready",
    dot: "#10b981",
  },
  Failed: {
    accent: "#b42318",
    bg: "#fef3f2",
    border: "#fecdca",
    icon: <CloseCircleFilled />,
    label: "Failed",
    dot: "#ef4444",
  },
};

function getStatusMeta(status: string | undefined) {
  return (
    STATUS_META[(status as StatusName) ?? ""] ?? {
      accent: "#475467",
      bg: "#f2f4f7",
      border: "#d0d5dd",
      icon: null,
      label: status ?? "Unknown",
      dot: "#98a2b3",
    }
  );
}

function formatDateTime(iso?: string): string {
  if (!iso) return "";
  return moment(iso).format("DD MMM YYYY, HH:mm");
}

const ExportNotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const {result, loading} = useSelector(getExportRequestList);
  const hasFetchedOnceRef = useRef(false);

  const items: TYPES.ExportRequestResponse[] = useMemo(
    () => result?.items ?? [],
    [result],
  );
  const inProgressCount = useMemo(
    () =>
      items.filter(
        i => i.status === "Pending" || i.status === "Processing",
      ).length,
    [items],
  );
  const completedCount = useMemo(
    () => items.filter(i => i.status === "Completed").length,
    [items],
  );

  const fetchList = useCallback(() => {
    dispatch(
      getExportRequestListRequest({
        pagination: {pageNumber: 1, pageLimit: 10},
        sort: [{field: "requestedDatetime", order: "desc"}],
      }),
    );
    hasFetchedOnceRef.current = true;
  }, [dispatch]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) fetchList();
  };

  useEffect(() => {
    if (!open) return;
    if (inProgressCount === 0) return;
    const id = window.setInterval(fetchList, POLL_MS);
    return () => window.clearInterval(id);
  }, [open, inProgressCount, fetchList]);

  const handleClickItem = (it: TYPES.ExportRequestResponse) => {
    if (it.status === "Completed" && it.url) {
      window.open(it.url, "_blank", "noopener,noreferrer");
    }
  };

  const isInitialLoading = loading && !hasFetchedOnceRef.current;

  // ───── Header ─────────────────────────────────────────────────────
  const header = (
    <div
      style={{
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
        padding: "16px 18px",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -30,
          right: -30,
          width: 110,
          height: 110,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -40,
          right: 30,
          width: 70,
          height: 70,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: 0.2,
              marginBottom: 2,
            }}
          >
            Exports
          </div>
          <div
            style={{
              fontSize: 12,
              opacity: 0.85,
              fontWeight: 400,
            }}
          >
            {inProgressCount > 0 ? (
              <>
                <LoadingOutlined spin style={{marginRight: 6}} />
                {inProgressCount} processing
              </>
            ) : items.length === 0 ? (
              "No requests yet"
            ) : (
              `${completedCount} ready to download`
            )}
          </div>
        </div>
        <Tooltip title="Refresh">
          <Button
            type="text"
            size="small"
            icon={
              <ReloadOutlined
                spin={loading}
                style={{color: "#fff", fontSize: 14}}
              />
            }
            onClick={fetchList}
            disabled={loading}
            style={{
              color: "#fff",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 8,
              height: 32,
              width: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </Tooltip>
      </div>
    </div>
  );

  // ───── Row ────────────────────────────────────────────────────────
  const renderItem = (it: TYPES.ExportRequestResponse) => {
    const meta = getStatusMeta(it.status);
    const isClickable = it.status === "Completed" && !!it.url;

    return (
      <div
        key={it.id}
        onClick={() => handleClickItem(it)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          borderRadius: 12,
          cursor: isClickable ? "pointer" : "default",
          transition: "all 0.18s ease",
          background: "#fff",
          border: "1px solid #f2f4f7",
          marginBottom: 8,
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={e => {
          if (isClickable) {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = meta.border;
            el.style.boxShadow = `0 4px 12px -4px ${meta.bg}, 0 0 0 1px ${meta.border}`;
            el.style.transform = "translateY(-1px)";
          }
        }}
        onMouseLeave={e => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = "#f2f4f7";
          el.style.boxShadow = "none";
          el.style.transform = "translateY(0)";
        }}
      >
        {/* Left status accent bar */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: meta.dot,
          }}
        />

        {/* Icon tile */}
        <div
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 10,
            background: meta.bg,
            border: `1px solid ${meta.border}`,
            color: meta.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            position: "relative",
          }}
        >
          <FileExcelOutlined style={{fontSize: 18}} />
          <div
            style={{
              position: "absolute",
              bottom: -4,
              right: -4,
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#fff",
              border: `1.5px solid ${meta.border}`,
              color: meta.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
            }}
          >
            {meta.icon}
          </div>
        </div>

        {/* Body */}
        <div style={{flex: 1, minWidth: 0}}>
          <Tooltip title={it.fileName} mouseEnterDelay={0.4}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#101828",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                marginBottom: 4,
              }}
            >
              {it.fileName ?? "Excel export"}
            </div>
          </Tooltip>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                lineHeight: "18px",
                padding: "0 8px",
                background: meta.bg,
                color: meta.accent,
                border: `1px solid ${meta.border}`,
                borderRadius: 999,
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: meta.dot,
                  display: "inline-block",
                }}
              />
              {meta.label}
            </span>
            <Typography.Text
              type="secondary"
              style={{fontSize: 11, color: "#667085"}}
            >
              {formatDateTime(it.requestedDatetime)}
            </Typography.Text>
          </div>
        </div>

        {/* Trailing action */}
        {isClickable ? (
          <Tooltip title="Download">
            <Button
              type="primary"
              size="small"
              shape="circle"
              icon={<DownloadOutlined />}
              onClick={e => {
                e.stopPropagation();
                handleClickItem(it);
              }}
              style={{
                background: BRAND,
                borderColor: BRAND,
                boxShadow: `0 2px 6px -1px ${BRAND}55`,
              }}
            />
          </Tooltip>
        ) : null}
      </div>
    );
  };

  // ───── Body ───────────────────────────────────────────────────────
  const body = isInitialLoading ? (
    <div style={{padding: "12px 14px"}}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            padding: "12px",
            borderRadius: 12,
            border: "1px solid #f2f4f7",
            marginBottom: 8,
          }}
        >
          <Skeleton
            active
            avatar={{shape: "square", size: 40}}
            paragraph={{rows: 1, width: ["55%"]}}
            title={{width: "75%"}}
          />
        </div>
      ))}
    </div>
  ) : items.length === 0 ? (
    <div
      style={{
        padding: "44px 12px 36px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#f2f4f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#98a2b3",
          fontSize: 28,
        }}
      >
        <InboxOutlined />
      </div>
      <div style={{textAlign: "center"}}>
        <Typography.Text strong style={{fontSize: 14, color: "#344054"}}>
          No exports yet
        </Typography.Text>
        <div
          style={{
            fontSize: 12,
            color: "#667085",
            marginTop: 4,
            maxWidth: 240,
          }}
        >
          When you request a reading export, it'll show up here.
        </div>
      </div>
    </div>
  ) : (
    <div
      className="export-bell-scroll"
      style={{
        maxHeight: POPOVER_MAX_HEIGHT - 90,
        overflowY: "auto",
        padding: "12px 14px 6px",
      }}
    >
      {items.map(renderItem)}
    </div>
  );

  // ───── Footer ─────────────────────────────────────────────────────
  const footer = items.length > 0 ? (
    <div
      style={{
        padding: "10px 16px",
        borderTop: "1px solid #f2f4f7",
        background: "#fafbfc",
        fontSize: 11,
        color: "#667085",
        textAlign: "center",
      }}
    >
      Showing latest {items.length} export{items.length === 1 ? "" : "s"}
    </div>
  ) : null;

  const content = (
    <div style={{width: POPOVER_WIDTH}}>
      {header}
      {body}
      {footer}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      overlayInnerStyle={{
        padding: 0,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow:
          "0 20px 25px -5px rgba(16, 24, 40, 0.10), 0 8px 10px -6px rgba(16, 24, 40, 0.08)",
      }}
    >
      <Badge
        count={inProgressCount}
        size="small"
        offset={[-2, 2]}
        style={{
          boxShadow: inProgressCount > 0 ? "0 0 0 2px #fff" : undefined,
        }}
      >
        <BellOutlined
          style={{
            fontSize: 22,
            cursor: "pointer",
            color: "#fff",
            transition: "transform 0.2s ease",
          }}
          onMouseEnter={e =>
            ((e.currentTarget as HTMLElement).style.transform = "scale(1.1)")
          }
          onMouseLeave={e =>
            ((e.currentTarget as HTMLElement).style.transform = "scale(1)")
          }
        />
      </Badge>
    </Popover>
  );
};

export default ExportNotificationBell;
